import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

/*
 * 공용 Vendor 페이지 디자인 시스템
 * Delfort / Siwoon 에서 확립한 레이아웃을 다른 vendor 소개 페이지도
 * 일관되게 사용할 수 있도록 추출한 재사용 컴포넌트 모음.
 */

/* ---------- Page ---------- */
export const Page = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  color: ${({ theme }) => theme.COLOR.BLACK};
`

/* ---------- Hero (navy gradient) ---------- */
const HeroSection = styled.section`
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(135deg, #000063 0%, #0a2a6b 52%, #3e7edb 100%);
  display: flex;
  align-items: flex-end;
  min-height: 380px;

  @media (max-width: 900px) {
    min-height: 300px;
  }
`

const HeroInner = styled.div`
  padding: 56px 13%;
  width: 100%;

  @media (max-width: 900px) {
    padding: 40px 24px;
  }
`

const Badge = styled.div`
  display: inline-block;
  padding: 7px 16px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: ${({ theme }) => theme.COLOR.WHITE};
  font-size: ${({ theme }) => theme.SIZE.SMALL};
  font-weight: 700;
  letter-spacing: 0.02em;
  margin-bottom: 20px;
`

const HeroTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.COLOR.WHITE};
  font-size: ${({ theme }) => theme.SIZE.X1LARGE};
  font-weight: 700;
  line-height: 1.25;
  max-width: 960px;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.X05LARGE};
  }
`

const HeroSubtitle = styled.p`
  margin: 16px 0 0;
  color: rgba(236, 241, 255, 0.94);
  font-size: ${({ theme }) => theme.SIZE.LARGE};
  line-height: 1.6;
  max-width: 820px;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  }
`

export const VendorHero = ({ title, subtitle, badge }) => (
  <HeroSection>
    <HeroInner>
      {badge ? <Badge>{badge}</Badge> : null}
      <HeroTitle>{title}</HeroTitle>
      {subtitle ? <HeroSubtitle>{subtitle}</HeroSubtitle> : null}
    </HeroInner>
  </HeroSection>
)

/* ---------- Main / Sections ---------- */
export const Main = styled.div`
  padding: 80px 13%;
  display: flex;
  flex-direction: column;
  gap: 72px;

  @media (max-width: 1200px) {
    padding: 64px 8%;
    gap: 60px;
  }

  @media (max-width: 900px) {
    padding: 48px 24px;
    gap: 52px;
  }
`

export const Eyebrow = styled.div`
  color: ${({ theme }) => theme.COLOR.MAGORELLEBLUE};
  font-size: ${({ theme }) => theme.SIZE.SMALL};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-bottom: 12px;
`

export const SectionTitle = styled.h2`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.SIZE.X05LARGE};
  font-weight: 600;
  line-height: 1.3;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.XLARGE};
  }
`

export const SubTitle = styled.h3`
  margin: 48px 0 20px;
  font-size: ${({ theme }) => theme.SIZE.XLARGE};
  font-weight: 700;
  line-height: 1.3;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.LARGE};
  }
`

export const Lead = styled.p`
  margin: 0;
  line-height: 1.8;
  font-size: ${({ theme }) => theme.SIZE.MEDIUM2};
  max-width: 940px;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  }
`

export const BodyText = styled.p`
  margin: 0 0 16px;
  line-height: 1.8;
  font-size: ${({ theme }) => theme.SIZE.MEDIUM2};

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  }
`

export const Section = styled.section``

/* ---------- Split (text + image) ---------- */
export const Split = styled.section`
  display: flex;
  flex-direction: ${({ $imageRight }) => ($imageRight ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 48px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 24px;
    align-items: stretch;
  }
`

export const Media = styled.div`
  flex: 1 1 0;
  min-width: 0;
`

export const Text = styled.div`
  flex: 1 1 0;
  min-width: 0;
`

export const SectionImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.COLOR.RIGHTGRAY};
  box-shadow: 0 14px 34px rgba(15, 23, 66, 0.1);
`

/* ---------- Cards ---------- */
export const CardRow = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

export const Card = styled.div`
  flex: 1 1 0;
  background-color: #f5f8fd;
  border: 1px solid rgba(62, 126, 219, 0.16);
  border-radius: 14px;
  padding: 26px;
`

export const CardTitle = styled.h3`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.COLOR.MAGORELLEBLUE};
  font-size: ${({ theme }) => theme.SIZE.LARGE};
  font-weight: 700;
  line-height: 1.4;
`

export const CardText = styled.div`
  line-height: 1.7;
  font-size: ${({ theme }) => theme.SIZE.MEDIUM};
`

/* ---------- Image card (image on top + caption) ---------- */
const Figure = styled.figure`
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  border: 1px solid rgba(62, 126, 219, 0.16);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 26px rgba(15, 23, 66, 0.08);
`

const FigureImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  background-color: #f5f8fd;
`

const FigureCaption = styled.figcaption`
  padding: 16px 18px;
  text-align: center;
  font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  font-weight: 600;
  line-height: 1.5;
`

export const ImageCard = ({ src, alt, caption }) => (
  <Figure>
    <FigureImg src={src} alt={alt || ''} />
    {caption ? <FigureCaption>{caption}</FigureCaption> : null}
  </Figure>
)

/* ---------- Chips ---------- */
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border-radius: 999px;
  background-color: #f5f8fd;
  border: 1px solid rgba(62, 126, 219, 0.28);
  color: ${({ theme }) => theme.COLOR.DARKBLUE};
  font-size: ${({ theme }) => theme.SIZE.MEDIUM2};
  font-weight: 600;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  }
`

/* ---------- Spec table ---------- */
const SpecCaption = styled.div`
  color: ${({ theme }) => theme.COLOR.DARKBLUE};
  font-size: ${({ theme }) => theme.SIZE.MEDIUM2};
  font-weight: 700;
  margin: 28px 0 12px;
`

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.SIZE.MEDIUM};

  th, td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.COLOR.RIGHTGRAY};
    vertical-align: top;
  }

  tbody td:first-child {
    color: ${({ theme }) => theme.COLOR.DARKBLUE};
    font-weight: 600;
    white-space: nowrap;
    width: 34%;
  }

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.SMALL};
    th, td { padding: 10px 12px; }
  }
`

export const SpecTable = ({ caption, rows }) => (
  <>
    {caption ? <SpecCaption>{caption}</SpecCaption> : null}
    <TableWrap>
      <StyledTable>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r[0]}</td>
              <td>{r[1]}</td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </TableWrap>
  </>
)

/* ---------- CTA ---------- */
const CtaSection = styled.section`
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(135deg, #000063 0%, #123a86 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 72px 24px;
`

const CtaInner = styled.div`
  text-align: center;
  max-width: 720px;
`

const CtaEyebrow = styled.div`
  color: rgba(236, 241, 255, 0.9);
  font-size: ${({ theme }) => theme.SIZE.MEDIUM};
  font-weight: 600;
  margin-bottom: 14px;
`

const CtaTitle = styled.h2`
  margin: 0 0 26px;
  color: ${({ theme }) => theme.COLOR.WHITE};
  font-size: ${({ theme }) => theme.SIZE.X05LARGE};
  font-weight: 700;

  @media (max-width: 900px) {
    font-size: ${({ theme }) => theme.SIZE.XLARGE};
  }
`

const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 14px 44px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  color: ${({ theme }) => theme.COLOR.DARKBLUE};
  font-size: ${({ theme }) => theme.SIZE.MEDIUM2};
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.18s ease, background-color 0.18s ease;

  &:hover {
    background-color: ${({ theme }) => theme.COLOR.MAGORELLEBLUE};
    color: ${({ theme }) => theme.COLOR.WHITE};
    transform: translateY(-2px);
  }
`

export const VendorCta = ({ note, title, buttonLabel = '문의하기', to = '/contact' }) => (
  <CtaSection>
    <CtaInner>
      {note ? <CtaEyebrow>{note}</CtaEyebrow> : null}
      <CtaTitle>{title}</CtaTitle>
      <CtaButton to={to}>{buttonLabel}</CtaButton>
    </CtaInner>
  </CtaSection>
)
